import type {
  IAttachment,
  IListMessageResponse,
  IListMessagesResponse,
  IMessageResponse
} from '@/types/services/message.service';
import type {
  IMassAction,
  IMassActionAllResultsMessage,
  IMassActionBaseRequest,
  IMassActionMessage,
  IMassActionProgressData
} from '@/types/services/massAction.service';
import { ROUTE_NAMES } from '@/router/conf';
import type { RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import type { QueryClient, QueryKey } from '@tanstack/vue-query';
import type { IAttributesResponse } from '@/types/services/user.service';
import {
  isIMailboxAllResultsSelection,
  isISearchAllResultsSelection
} from '@/types/components/mailbox-shared/mass-actions/shared.consts';

export const getMessageKey = (
  message: Pick<IListMessageResponse | IMessageResponse, 'mailbox' | 'thread' | 'id'>
) => {
  return `${message.mailbox}/${message.thread}/${message.id}`;
};

export const isSeen = (message: Pick<IListMessageResponse | IMessageResponse, 'seen'>) => {
  return message.seen;
};
export const isFlagged = (message: Pick<IListMessageResponse | IMessageResponse, 'flagged'>) => {
  return message.flagged;
};
export const isUnSeen = (message: Pick<IListMessageResponse | IMessageResponse, 'seen'>) => {
  return !isSeen(message);
};
export const isUnFlagged = (message: Pick<IListMessageResponse | IMessageResponse, 'flagged'>) => {
  return !isFlagged(message);
};

/**
 * Computes the state a collapsed (threading) list item should display for a thread: seen
 * only when every message in the thread is seen, and flagged when any message in the
 * thread is flagged.
 *
 * `threadResults` is the thread's per-message data from the thread cache, already
 * reflecting the change, or undefined when the full thread is unknown (the thread is not
 * cached, or the cached thread is missing the changed message). In that case only the
 * guaranteed directions can be reported: `sureUnseen`/`sureFlagged` mean at least one
 * message in the thread is known to be unseen/flagged, which always wins. A null value
 * means the item should keep its current state until the server recomputes it.
 */
export const getCollapsedThreadItemState = (
  threadResults: Pick<IListMessageResponse, 'seen' | 'flagged'>[] | undefined,
  sureUnseen: boolean,
  sureFlagged: boolean
): { seen: boolean | null; flagged: boolean | null } => {
  if (!threadResults) {
    return {
      seen: sureUnseen ? false : null,
      flagged: sureFlagged ? true : null
    };
  }

  return {
    seen: !threadResults.some((message) => !message.seen),
    flagged: threadResults.some((message) => message.flagged)
  };
};

export const messageToMassActionMessage = (
  message: IListMessageResponse | IMessageResponse
): IMassActionMessage => {
  return {
    messageId: message.id,
    messageUID: message.messageId,
    threadId: message.thread,
    mailboxId: message.mailbox,
    from: message.from,
    flagged: message.flagged,
    seen: message.seen
  };
};

export const messageToMassActionAllResultsMessage = (
  message: IListMessageResponse | IMessageResponse
): IMassActionAllResultsMessage => {
  return {
    mailboxId: message.mailbox,
    messageId: '*'
  };
};

export const isInlineAttachment = (attachment: IAttachment): boolean => {
  return (
    attachment.disposition === 'inline' &&
    attachment.contentType.startsWith('image/') &&
    attachment.contentType !== 'image/tiff' &&
    attachment.related
  );
};

const newLineRegex = new RegExp('[\\n\\r]', 'gm');

// Replace whitespaces within style attributes
const replacedWhitespacesInStyle = (input: string) =>
  input.replace(/style="([^"]*)"/g, (match, p1) => {
    return `style="${p1.replace(/\s+/g, '')}"`;
  });
export const htmlDecode = (input: string) => {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.body.innerHTML ?? '';
};
export const cleanHTML = (html: string) => {
  return replacedWhitespacesInStyle(html.replace(newLineRegex, ''));
};

/**
 * Normalizes HTML for string comparison by ensuring consistent serialization format.
 * This handles differences in self-closing tags (<br> vs <br />), HTML entity encoding
 * (&nbsp; vs actual spaces), and other serialization variations.
 * @param html - The HTML string to normalize.
 * @returns Normalized HTML string suitable for string comparison.
 */
export const normalizeHTMLForComparison = (html: string): string => {
  // Use DOMParser to parse and re-serialize HTML for consistent format
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const normalized = doc.body.innerHTML;

  // Apply existing cleanHTML logic (remove newlines, whitespace in styles)
  return cleanHTML(normalized);
};

export const isSameMessage = (
  first: IListMessageResponse | IMessageResponse,
  second: IListMessageResponse | IMessageResponse
) => {
  return (
    first.id === second.id && first.mailbox === second.mailbox && first.thread === second.thread
  );
};
export const isMassActionMessageSameMessage = (
  first: IMassActionMessage,
  second: IListMessageResponse | IMessageResponse
) => {
  return (
    first.messageId === second.id &&
    first.mailboxId === second.mailbox &&
    first.threadId === second.thread
  );
};

export const isThreadView = (route: RouteLocationNormalizedLoadedGeneric) =>
  route.matched.some(
    (route) =>
      route.name?.toString().includes(ROUTE_NAMES.MAILBOX_THREAD) ||
      route.name?.toString().includes(ROUTE_NAMES.MAILBOX_SEARCH_THREAD)
  );

export const isSearchView = (route: RouteLocationNormalizedLoadedGeneric) =>
  route.matched.some((route) => route.name === ROUTE_NAMES.MAILBOX_SEARCH);

export const parseFileName = (inputFileName: string) => {
  const lastDotIndex = inputFileName.lastIndexOf('.');
  const fileName = lastDotIndex !== -1 ? inputFileName.substring(0, lastDotIndex) : inputFileName;
  const extension = lastDotIndex !== -1 ? inputFileName.substring(lastDotIndex + 1) : undefined;
  const lowerExtension = extension?.toLowerCase() ?? '';
  return {
    fileName,
    extension,
    lowerExtension
  };
};

export const minimizeFileName = (
  inputFileName: string,
  extension: string | undefined,
  toDisplayBeforeExtension = 16
) => {
  if (inputFileName.length > toDisplayBeforeExtension) {
    const substringLength = Math.max(1, toDisplayBeforeExtension - (extension?.length ?? 0));
    return `${inputFileName.substring(0, substringLength)}${extension ? `...${extension}` : ''}`;
  }
  return `${inputFileName}${extension ? `.${extension}` : ''}`;
};

/**
 * Returns the partial query key bases for mailbox and search list queries.
 * Used for functions that don't need the full query keys, but just the base.
 */
export const getMailboxAndSearchListQueriesKeyBases = (
  currentUserIndex: string,
  mailboxId?: string
) => {
  return [
    mailboxId
      ? ['authenticatedQuery', currentUserIndex, 'mailbox', mailboxId, 'list']
      : ['authenticatedQuery', currentUserIndex, 'mailbox'],
    ['authenticatedQuery', currentUserIndex, 'search', 'query']
  ];
};

/**
 * Returns the partial query key bases for all of the mailboxes that contain messages in the thread.
 */
export const getThreadMailboxQueriesKeyBases = (
  queryClient: QueryClient,
  currentUserIndex: string,
  threadId: string
) => {
  const threadData = queryClient.getQueryData<IListMessagesResponse>([
    'authenticatedQuery',
    currentUserIndex,
    'thread',
    threadId
  ]);
  return (
    threadData?.results.map(({ mailbox }) => {
      return ['authenticatedQuery', currentUserIndex, 'mailbox', mailbox, 'list'];
    }) ?? []
  );
};

/**
 * Invalidates the mailbox and search list queries for a given mailbox. Used as the single
 * reconciliation point after a batch of optimistically-applied mass action events settles,
 * instead of invalidating on every individual EventSource event.
 */
export const invalidateMailboxAndSearchListQueries = (
  queryClient: QueryClient,
  currentUserIndex: string,
  mailboxId?: string
) => {
  getMailboxAndSearchListQueriesKeyBases(currentUserIndex, mailboxId).forEach((queryKey) => {
    queryClient.invalidateQueries({ queryKey, exact: false });
  });
};

/**
 * Returns the full query keys for mailbox and search list queries.
 * This is used to get the actual query keys from the query client.
 * It will return all queries that match the base query keys.
 */
export const getMailboxAndSearchListQueriesKeys = (
  queryClient: QueryClient,
  currentUserIndex: string,
  mailboxId?: string
) => {
  return getMailboxAndSearchListQueriesKeyBases(currentUserIndex, mailboxId).flatMap((queryKey) => {
    const queries = queryClient.getQueriesData({ queryKey });
    return queries.map(([key]) => key);
  });
};

// The attributes mutation (useUserService) applies new attribute values to the query cache
// optimistically before the debounced PATCH reaches the server. By the time the
// ATTRIBUTES_UPDATED event arrives, the cache in the tab that initiated the change already
// matches the event payload, so the change cannot be detected from the cache alone. To make
// it detectable, the value the attribute had before the optimistic update is registered here.
let threadingShownStateBeforeUpdate: boolean | undefined;

export const registerThreadingShownStateBeforeUpdate = (value: boolean | undefined) => {
  threadingShownStateBeforeUpdate = value;
};

export const getThreadingShownStateBeforeUpdate = (): boolean | undefined => {
  return threadingShownStateBeforeUpdate;
};

export const clearThreadingShownStateBeforeUpdate = () => {
  threadingShownStateBeforeUpdate = undefined;
};

/**
 * Returns the mailbox and search list query keys that need to be invalidated when an
 * ATTRIBUTES_UPDATED event arrives.
 *
 * The mailbox list and search responses are computed with the user's threadingShownState
 * (thread collapsing), so the cached lists are stale whenever the server-confirmed value
 * differs from the value the lists were fetched with. When the change was initiated in this
 * tab, the pre-update value registered by the attributes mutation is used as the baseline;
 * otherwise (change from another tab or device) the currently cached value is used.
 */
export const getMailboxAndSearchListQueriesKeysToInvalidateOnThreadingChange = (
  queryClient: QueryClient,
  currentUserIndex: string,
  newAttributes: Pick<IAttributesResponse, 'threadingShownState'>
): QueryKey[] => {
  const cachedAttributes = queryClient.getQueryData<IAttributesResponse>([
    'authenticatedQuery',
    currentUserIndex,
    'user',
    'attributes'
  ]);
  const threadingShownStateBeforeChange =
    threadingShownStateBeforeUpdate ?? cachedAttributes?.threadingShownState;

  if (
    threadingShownStateBeforeChange === undefined ||
    threadingShownStateBeforeChange === newAttributes.threadingShownState
  ) {
    return [];
  }

  clearThreadingShownStateBeforeUpdate();
  return getMailboxAndSearchListQueriesKeys(queryClient, currentUserIndex);
};

export const getMessageThreadRedirectParams = (
  route: RouteLocationNormalizedLoadedGeneric,
  message: IListMessageResponse,
  userAttributes?: IAttributesResponse
) => {
  if ((message.threadMessageCount === 1 || !userAttributes?.threadingShownState) && message.draft) {
    return {
      name: ROUTE_NAMES.COMPOSE,
      params: {
        userIndex: route.params.userIndex,
        draftId: message.id
      }
    };
  }
  return {
    name: isSearchView(route) ? ROUTE_NAMES.MAILBOX_SEARCH_THREAD : ROUTE_NAMES.MAILBOX_THREAD,
    params: {
      userIndex: route.params.userIndex,
      mailboxId: message.mailbox,
      threadId: message.thread,
      messageId: message.id
    },
    query: { ...route.query }
  };
};

export const isMovingMassAction = (progressData: IMassActionProgressData | null | undefined) => {
  return (
    progressData?.action === 'moveTo' ||
    progressData?.action === 'spam' ||
    progressData?.action === 'trash' ||
    progressData?.action === 'archive' ||
    progressData?.action === 'delete'
  );
};

export const mailboxMassActionContainsRequest = (
  progressData: IMassActionProgressData | null | undefined,
  request: IMassActionBaseRequest,
  action: IMassAction
) => {
  if (!progressData) {
    return false;
  }
  // Only block the exact same action type, not all moving actions
  if (progressData.action !== action) {
    return false;
  }
  if (isISearchAllResultsSelection(request) || isIMailboxAllResultsSelection(request)) {
    return true;
  }
  return progressData.messages.some(({ mailboxId, messageId }) => {
    if (messageId === '*') {
      return true;
    }
    return request.messages.some(
      (message) => message.messageId === messageId && message.mailboxId === mailboxId
    );
  });
};
